"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { GripVertical, ImagePlus, Link2, Lock, Plus, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntryDrawerHeader } from "@/components/wiki/entry-drawer-header";
import { EntryPages, type DrawerPage } from "@/components/wiki/entry-pages";
import { trackDemoImageUrl } from "@/lib/demo/demo-image-urls";
import { MAX_COVER_FILE_SIZE } from "@/lib/r2/cover-image-validation";
import { categoryHex } from "@/lib/wiki-config";
import { useDemoStore } from "@/stores/demo-store";
import type { GateReason } from "./demo-gate-modal";
import { DemoWikiPageEditor } from "./demo-wiki-page-editor";
export function DemoWikiEntryPanel(t0) {
  const $ = _c(144);
  const {
    entryId,
    onClose,
    onSelectEntry,
    onOpenGate
  } = t0;
  let t1;
  if ($[0] !== entryId) {
    t1 = state => state.lore.find(item => item.id === entryId);
    $[0] = entryId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const entry = useDemoStore(t1);
  const lore = useDemoStore(_temp);
  const categories = useDemoStore(_temp2);
  const connections = useDemoStore(_temp3);
  const updateEntry = useDemoStore(_temp4);
  const addEntry = useDemoStore(_temp5);
  const deleteEntry = useDemoStore(_temp6);
  const addConnection = useDemoStore(_temp7);
  const deleteConnection = useDemoStore(_temp8);
  const inputRef = useRef(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [addingConnection, setAddingConnection] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [relation, setRelation] = useState("");
  const [width, setWidth] = useState(540);
  const [isResizing, setIsResizing] = useState(false);
  let t2;
  let t3;
  if ($[2] !== isResizing) {
    t2 = () => {
      if (!isResizing) {
        return;
      }
      const handleMouseMove = function handleMouseMove(event) {
        setWidth(Math.min(900, Math.max(360, window.innerWidth - event.clientX)));
      };
      const handleMouseUp = function handleMouseUp() {
        setIsResizing(false);
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    };
    t3 = [isResizing];
    $[2] = isResizing;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== entry || $[6] !== lore) {
    t4 = entry?.parentEntryId ? lore.find(item_0 => item_0.id === entry.parentEntryId) : entry;
    $[5] = entry;
    $[6] = lore;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const rootEntry = t4;
  let t5;
  if ($[8] !== lore || $[9] !== rootEntry) {
    t5 = rootEntry ? lore.filter(item_1 => item_1.parentEntryId === rootEntry.id) : [];
    $[8] = lore;
    $[9] = rootEntry;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const childPages = t5;
  let t6;
  if ($[11] !== childPages || $[12] !== rootEntry) {
    t6 = rootEntry ? [{
      id: rootEntry.id,
      title: "Overview",
      isPublic: false,
      isOverview: true
    }, ...childPages.map(_temp9)] : [];
    $[11] = childPages;
    $[12] = rootEntry;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const drawerPages = t6;
  let t7;
  if ($[14] !== connections || $[15] !== rootEntry) {
    t7 = rootEntry ? connections.filter(connection => connection.sourceEntryId === rootEntry.id || connection.targetEntryId === rootEntry.id) : [];
    $[14] = connections;
    $[15] = rootEntry;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const entryConnections = t7;
  if (!entry || !rootEntry) {
    return null;
  }
  const selectedEntryId = entry.id;
  const rootEntryId = rootEntry.id;
  const rootCategoryId = rootEntry.categoryId;
  let t8;
  if ($[17] !== addEntry || $[18] !== onSelectEntry || $[19] !== pageTitle || $[20] !== rootCategoryId || $[21] !== rootEntryId) {
    t8 = function createPage() {
      const title = pageTitle.trim();
      if (!title) {
        return;
      }
      const pageId = addEntry(rootCategoryId, title, rootEntryId);
      setPageTitle("");
      setShowAddPage(false);
      if (pageId) {
        onSelectEntry(pageId);
      }
    };
    $[17] = addEntry;
    $[18] = onSelectEntry;
    $[19] = pageTitle;
    $[20] = rootCategoryId;
    $[21] = rootEntryId;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const createPage = t8;
  let t9;
  if ($[23] !== selectedEntryId || $[24] !== updateEntry) {
    t9 = function uploadLocalImage(file) {
      if (!file.type.match(/^image\/(jpeg|png|webp|gif)$/)) {
        return;
      }
      if (file.size <= 0 || file.size > MAX_COVER_FILE_SIZE) {
        return;
      }
      const url = trackDemoImageUrl(URL.createObjectURL(file));
      updateEntry(selectedEntryId, {
        imageUrl: url
      });
    };
    $[23] = selectedEntryId;
    $[24] = updateEntry;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const uploadLocalImage = t9;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function resetConnectionForm() {
      setAddingConnection(false);
      setTargetId("");
      setRelation("");
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const resetConnectionForm = t10;
  const t11 = `${width}px`;
  let t12;
  if ($[27] !== t11) {
    t12 = {
      "--drawer-width": t11
    };
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = t12 as React.CSSProperties;
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setIsResizing(true);
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = `hidden w-1.5 shrink-0 cursor-col-resize items-center justify-center transition-colors hover:bg-primary/20 md:flex ${isResizing ? "bg-primary/30" : ""}`;
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <GripVertical className="h-4 w-4 text-muted-foreground/30" />;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15) {
    t17 = <div aria-hidden={true} onMouseDown={t14} className={t15}>{t16}</div>;
    $[31] = t15;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [];
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== onClose || $[35] !== onSelectEntry || $[36] !== rootEntry.name) {
    t19 = <EntryDrawerHeader breadcrumbs={t18} entryName={rootEntry.name} onSelectEntry={onSelectEntry} onClose={onClose} />;
    $[34] = onClose;
    $[35] = onSelectEntry;
    $[36] = rootEntry.name;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Label className="text-xs" htmlFor="demo-entry-name">Name</Label>;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const t21 = entry.id !== rootEntry.id;
  let t22;
  if ($[39] !== rootEntry.id || $[40] !== updateEntry) {
    t22 = event_0 => updateEntry(rootEntry.id, {
      name: event_0.target.value
    });
    $[39] = rootEntry.id;
    $[40] = updateEntry;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== rootEntry.name || $[43] !== t21 || $[44] !== t22) {
    t23 = <div className="space-y-1.5 px-4 pb-2 pt-4">{t20}<Input id="demo-entry-name" value={rootEntry.name} disabled={t21} onChange={t22} className="h-8 text-sm" /></div>;
    $[42] = rootEntry.name;
    $[43] = t21;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== categories || $[47] !== rootEntry.categoryId || $[48] !== rootEntry.id || $[49] !== updateEntry) {
    t24 = categories.length > 1 && <div className="space-y-1.5 px-4 pb-2"><Label className="text-xs">Category</Label><Select value={rootEntry.categoryId} onValueChange={categoryId => updateEntry(rootEntry.id, {
        categoryId
      })}><SelectTrigger className="h-8 w-full text-xs"><SelectValue placeholder={"Choose a category\u2026"} /></SelectTrigger><SelectContent>{categories.map(_temp10)}</SelectContent></Select></div>;
    $[46] = categories;
    $[47] = rootEntry.categoryId;
    $[48] = rootEntry.id;
    $[49] = updateEntry;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== deleteEntry || $[52] !== entry.id || $[53] !== onSelectEntry || $[54] !== rootEntry.id) {
    t25 = async pageId_0 => {
      deleteEntry(pageId_0);
      if (entry.id === pageId_0) {
        onSelectEntry(rootEntry.id);
      }
    };
    $[51] = deleteEntry;
    $[52] = entry.id;
    $[53] = onSelectEntry;
    $[54] = rootEntry.id;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== createPage || $[57] !== drawerPages || $[58] !== entry.id || $[59] !== onSelectEntry || $[60] !== pageTitle || $[61] !== showAddPage || $[62] !== t25) {
    t26 = <EntryPages pages={drawerPages} currentPageId={entry.id} showAddPage={showAddPage} newPageTitle={pageTitle} creatingPage={false} onSelectPage={onSelectEntry} onShowAddPage={setShowAddPage} onNewPageTitleChange={setPageTitle} onCreatePage={createPage} onDeletePage={t25} />;
    $[56] = createPage;
    $[57] = drawerPages;
    $[58] = entry.id;
    $[59] = onSelectEntry;
    $[60] = pageTitle;
    $[61] = showAddPage;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div><Label className="text-xs">Cover image</Label><p className="text-[10px] text-muted-foreground/60">Shown on this page. Demo images remain local to this tab.</p></div>;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== entry.id || $[66] !== entry.imageUrl || $[67] !== entry.name || $[68] !== onOpenGate || $[69] !== updateEntry) {
    t28 = entry.imageUrl ? <div className="group relative aspect-video w-full max-w-[320px] overflow-hidden rounded-lg border border-border/50 bg-background"><img src={entry.imageUrl} alt={`${entry.name} cover`} className="h-full w-full object-cover" /><div data-testid="demo-image-actions" className="absolute inset-0 flex items-end justify-center gap-2 bg-black/35 pb-2 opacity-100 transition-opacity sm:items-center sm:bg-black/60 sm:pb-0 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"><Button type="button" size="sm" variant="secondary" onClick={() => inputRef.current?.click()}><ImagePlus /> Replace</Button><Button type="button" size="icon-sm" variant="secondary" onClick={() => onOpenGate("wikiImage")} aria-label="Generate replacement image"><Sparkles /></Button><Button type="button" size="icon-sm" variant="destructive" onClick={() => updateEntry(entry.id, {
          imageUrl: null
        })} aria-label="Delete cover image"><Trash2 /></Button></div></div> : <div className="grid max-w-[320px] grid-cols-2 gap-2"><Button type="button" variant="outline" className="h-20 flex-col" onClick={() => inputRef.current?.click()}><ImagePlus /> Upload image</Button><Button type="button" variant="outline" className="h-20 flex-col border-primary/30 text-primary hover:bg-primary/5" onClick={() => onOpenGate("wikiImage")}><Sparkles /> Generate image</Button></div>;
    $[65] = entry.id;
    $[66] = entry.imageUrl;
    $[67] = entry.name;
    $[68] = onOpenGate;
    $[69] = updateEntry;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== uploadLocalImage) {
    t29 = <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={event_1 => {
      const file_0 = event_1.target.files?.[0];
      event_1.target.value = "";
      if (file_0) {
        uploadLocalImage(file_0);
      }
    }} />;
    $[71] = uploadLocalImage;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t28 || $[74] !== t29) {
    t30 = <div className="space-y-2 px-4 py-2">{t27}{t28}{t29}</div>;
    $[73] = t28;
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="mb-1.5 flex items-center justify-between"><Label className="text-xs">Content</Label><span className="text-[10px] text-muted-foreground/60">Stored only in this tab</span></div>;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== entry.id || $[78] !== updateEntry) {
    t32 = (content, plainText) => updateEntry(entry.id, {
      content,
      description: plainText
    });
    $[77] = entry.id;
    $[78] = updateEntry;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== entry.content || $[81] !== entry.id || $[82] !== t32) {
    t33 = <div className="px-4 py-2">{t31}<DemoWikiPageEditor key={entry.id} initialContent={entry.content} onChange={t32} /></div>;
    $[80] = entry.content;
    $[81] = entry.id;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Lock className="size-4 shrink-0 text-muted-foreground" />;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="flex min-w-0 items-center gap-2">{t34}<div className="min-w-0"><p className="text-xs font-medium text-foreground">Make public</p><p className="text-[11px] leading-snug text-muted-foreground">Show this page on your story's public wiki.</p></div></div>;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== onOpenGate) {
    t36 = () => onOpenGate("wikiPublic");
    $[86] = onOpenGate;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <span className="absolute left-0.5 top-0.5 size-4 rounded-full bg-background shadow" />;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t36) {
    t38 = <div className="mt-2 space-y-2 border-t border-border/30 px-4 py-3"><div className="flex items-center justify-between gap-3">{t35}<button type="button" aria-label="Make page public" onClick={t36} title="We can't publish from the demo. Create an account for free to use this feature." className="relative h-5 w-9 shrink-0 cursor-not-allowed rounded-full bg-muted-foreground/30">{t37}</button></div></div>;
    $[89] = t36;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Label className="flex items-center gap-1 text-xs"><Link2 className="h-3 w-3" /> Connections</Label>;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== addingConnection) {
    t40 = !addingConnection && <button type="button" onClick={() => setAddingConnection(true)} className="flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/25"><Plus className="h-3 w-3" /> Link</button>;
    $[92] = addingConnection;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  let t41;
  if ($[94] !== t40) {
    t41 = <div className="flex items-center justify-between">{t39}{t40}</div>;
    $[94] = t40;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  let t42;
  if ($[96] !== addingConnection || $[97] !== deleteConnection || $[98] !== entryConnections || $[99] !== lore || $[100] !== rootEntry.id) {
    t42 = entryConnections.length === 0 && !addingConnection ? <p className="py-1 text-xs text-muted-foreground">No connections yet.</p> : <div className="space-y-0.5">{entryConnections.map(connection_0 => {
        const outgoing = connection_0.sourceEntryId === rootEntry.id;
        const otherId = outgoing ? connection_0.targetEntryId : connection_0.sourceEntryId;
        const other = lore.find(item_2 => item_2.id === otherId);
        return <div key={connection_0.id} className="flex items-center justify-between rounded-md border border-border/30 bg-background/50 px-2.5 py-1.5 text-xs"><div className="flex min-w-0 items-center gap-1.5"><span className="text-muted-foreground">{outgoing ? "\u2192" : "\u2190"}</span><span className="font-medium text-primary/70">{connection_0.relationLabel}</span><span className="truncate text-foreground">{other?.name ?? "Unknown"}</span></div><button type="button" onClick={() => deleteConnection(connection_0.id)} aria-label="Delete connection" className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /></button></div>;
      })}</div>;
    $[96] = addingConnection;
    $[97] = deleteConnection;
    $[98] = entryConnections;
    $[99] = lore;
    $[100] = rootEntry.id;
    $[101] = t42;
  } else {
    t42 = $[101];
  }
  let t43;
  if ($[102] !== addConnection || $[103] !== addingConnection || $[104] !== lore || $[105] !== relation || $[106] !== rootEntry.id || $[107] !== targetId) {
    t43 = addingConnection && <div className="space-y-1.5 rounded-lg border border-border/50 bg-muted/30 p-2"><div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[1fr_1fr_auto]"><select value={targetId} onChange={event_2 => setTargetId(event_2.target.value)} aria-label="Connection target" className="h-7 min-w-0 rounded-md border border-input bg-background px-2 text-xs"><option value="">Choose entry…</option>{lore.filter(item_3 => item_3.id !== rootEntry.id && !item_3.parentEntryId).map(_temp11)}</select><Input value={relation} onChange={event_3 => setRelation(event_3.target.value)} placeholder={"Relation \u2014 e.g. \"ally of\""} className="h-7 text-xs" /><Button size="sm" className="h-8 w-full px-2 text-xs sm:h-7 sm:w-auto" disabled={!targetId || !relation.trim()} onClick={() => {
          addConnection(rootEntry.id, targetId, relation.trim());
          resetConnectionForm();
        }}>Add</Button></div><button type="button" onClick={resetConnectionForm} className="text-[11px] text-muted-foreground hover:text-foreground">Cancel</button></div>;
    $[102] = addConnection;
    $[103] = addingConnection;
    $[104] = lore;
    $[105] = relation;
    $[106] = rootEntry.id;
    $[107] = targetId;
    $[108] = t43;
  } else {
    t43 = $[108];
  }
  let t44;
  if ($[109] !== t41 || $[110] !== t42 || $[111] !== t43) {
    t44 = <div className="space-y-2 px-4 py-2">{t41}{t42}{t43}</div>;
    $[109] = t41;
    $[110] = t42;
    $[111] = t43;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  let t45;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div className="mt-2 border-t border-border/30 px-4 py-2 text-[10px] text-muted-foreground/50">Temporary demo data · cleared on refresh</div>;
    $[113] = t45;
  } else {
    t45 = $[113];
  }
  let t46;
  if ($[114] !== t23 || $[115] !== t24 || $[116] !== t26 || $[117] !== t30 || $[118] !== t33 || $[119] !== t38 || $[120] !== t44) {
    t46 = <div className="flex-1 overflow-y-auto">{t23}{t24}{t26}{t30}{t33}{t38}{t44}{t45}</div>;
    $[114] = t23;
    $[115] = t24;
    $[116] = t26;
    $[117] = t30;
    $[118] = t33;
    $[119] = t38;
    $[120] = t44;
    $[121] = t46;
  } else {
    t46 = $[121];
  }
  const t47 = `Delete "${rootEntry.name}" and all its pages? This cannot be undone.`;
  let t48;
  if ($[122] !== deleteEntry || $[123] !== onClose || $[124] !== rootEntry.id) {
    t48 = async () => {
      deleteEntry(rootEntry.id);
      onClose();
    };
    $[122] = deleteEntry;
    $[123] = onClose;
    $[124] = rootEntry.id;
    $[125] = t48;
  } else {
    t48 = $[125];
  }
  let t49;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <button className="min-h-9 text-xs text-muted-foreground transition-colors hover:text-destructive sm:min-h-0">Delete entry</button>;
    $[126] = t49;
  } else {
    t49 = $[126];
  }
  let t50;
  if ($[127] !== t47 || $[128] !== t48) {
    t50 = <ConfirmDeleteDialog title="Delete Entry" description={t47} onConfirm={t48} trigger={t49} />;
    $[127] = t47;
    $[128] = t48;
    $[129] = t50;
  } else {
    t50 = $[129];
  }
  let t51;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <span className="hidden text-[10px] text-muted-foreground sm:inline">Changes update instantly</span>;
    $[130] = t51;
  } else {
    t51 = $[130];
  }
  let t52;
  if ($[131] !== onClose) {
    t52 = <div className="flex items-center gap-3">{t51}<Button size="sm" className="h-9 text-xs sm:h-7" onClick={onClose}>Done</Button></div>;
    $[131] = onClose;
    $[132] = t52;
  } else {
    t52 = $[132];
  }
  let t53;
  if ($[133] !== t50 || $[134] !== t52) {
    t53 = <footer className="flex shrink-0 items-center justify-between border-t border-border px-4 py-2">{t50}{t52}</footer>;
    $[133] = t50;
    $[134] = t52;
    $[135] = t53;
  } else {
    t53 = $[135];
  }
  let t54;
  if ($[136] !== t19 || $[137] !== t46 || $[138] !== t53) {
    t54 = <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{t19}{t46}{t53}</div>;
    $[136] = t19;
    $[137] = t46;
    $[138] = t53;
    $[139] = t54;
  } else {
    t54 = $[139];
  }
  let t55;
  if ($[140] !== t13 || $[141] !== t17 || $[142] !== t54) {
    t55 = <aside style={t13} data-testid="demo-wiki-entry-panel" className="absolute inset-0 z-30 flex w-full shrink-0 overflow-hidden bg-card md:static md:w-[var(--drawer-width)] md:border-l md:border-border md:bg-card/50">{t17}{t54}</aside>;
    $[140] = t13;
    $[141] = t17;
    $[142] = t54;
    $[143] = t55;
  } else {
    t55 = $[143];
  }
  return t55;
}
function _temp11(item_4) {
  return <option key={item_4.id} value={item_4.id}>{item_4.name}</option>;
}
function _temp10(category) {
  return <SelectItem key={category.id} value={category.id}><span className="flex items-center gap-2" style={{
      "--wiki-cat": categoryHex(category.category)
    } as React.CSSProperties}><span className="h-2 w-2 rounded-full bg-[var(--wiki-cat)]" />{category.label}</span></SelectItem>;
}
function _temp9(page) {
  return {
    id: page.id,
    title: page.name,
    isPublic: false,
    isOverview: false
  };
}
function _temp8(state_7) {
  return state_7.deleteLoreConnection;
}
function _temp7(state_6) {
  return state_6.addLoreConnection;
}
function _temp6(state_5) {
  return state_5.deleteLoreEntry;
}
function _temp5(state_4) {
  return state_4.addLoreEntry;
}
function _temp4(state_3) {
  return state_3.updateLoreEntry;
}
function _temp3(state_2) {
  return state_2.loreConnections;
}
function _temp2(state_1) {
  return state_1.loreCategories;
}
function _temp(state_0) {
  return state_0.lore;
}